import { DatePipe } from '@angular/common';
import { Component, OnInit, DoCheck, Input, Output, EventEmitter } from '@angular/core';
import moment from 'moment';
import { GamesService } from 'src/app/gamification/command-center/services/games.service';
import { BaremetricsCalendar } from 'src/app/reusables/baremetrics-calendar/baremetrics-calendar.component';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { cloneDeep, isEqual } from 'lodash';

@Component({
  selector: 'cv-configure-schedule',
  templateUrl: './configure-schedule.component.html',
  styleUrls: ['./configure-schedule.component.scss']
})
export class ConfigureScheduleComponent implements OnInit, DoCheck {
  @Input() step: number;
  @Input() componentOptions: any;
  @Output() componentComplete: EventEmitter<any> = new EventEmitter<any>();

  gs = null;
  IntervalTypes = Object.freeze({
    Day: "0",
    Week: "1",
    Month: "2"
  });
  consideredComplete: boolean = false;
  originalSchedule: any;
  schedule: any;
  oldInterval: string = "";
  earliestDate :any;
  isCxone = false;

  constructor(
    private datePipe: DatePipe,
    private gamesService: GamesService,
    private cvConfigurationService: CvConfigurationService
  ) {
    this.gs = this.gamesService.data;
  }

  ngOnInit(): void {
    this.gs.modified.item.Schedule = this.gs.modified.item.Schedule || {
      IntervalType: this.IntervalTypes.Day,
      Ongoing: false,
      StartDate: new Date()
    };
    
    this.gs.modified.item.Schedule.IntervalType = this.gs.modified.item.Schedule.IntervalType.toString();
    //Used for checking if the schedule has changed at all, which will consider the component "complete", thus unlocking the next component
    this.originalSchedule = cloneDeep(this.gs.modified.item.Schedule);
    this.schedule = this.gs.modified.item.Schedule;
    this.adjustStartDate();
    this.earliestDate = this.schedule.StartDate ? this.schedule.StartDate:new Date();
    if (!(this.schedule.EndDate == null && this.schedule.Ongoing)) {
      if (this.schedule.EndDate === undefined) {
        this.schedule.EndDate = null;
        this.originalSchedule.EndDate = null;
      }
      else {
        this.schedule.EndDate = new Date(this.schedule.EndDate);
        this.originalSchedule.EndDate = new Date(this.schedule.EndDate);
      }
    }
    this.isCxone = this.cvConfigurationService.isCxOne;
  }

  ngDoCheck() {
    if (this.originalSchedule != this.schedule) {
      if (this.oldInterval != "" && this.oldInterval != this.schedule.IntervalType) {
        this.oldInterval = this.schedule.IntervalType;

        //Reset the dates if the Interval Type was changed (filters don't affect a new type of Day)                        
        if (this.schedule.StartDate != null && !this._startDateFilter(this.schedule.StartDate)) {
          this.schedule.StartDate = null;
        }

        if (this.schedule.EndDate != null && (this.schedule.Ongoing || !(this._endDateFilter(this.schedule.EndDate)))) {
          this.schedule.EndDate = null;
        }
      }
      this._checkForComponentComplete();
    }
  }

  _checkForComponentComplete() {
    if (!this.consideredComplete) {
      if (!isEqual(this.schedule, this.originalSchedule)) {
        this.consideredComplete = true;

        var eventArgs = {
          currentStep: this.step - 1,
          unlocks: this.componentOptions.unlocks
        };

        this.componentComplete.emit(eventArgs);
      }
    }
  }

  _startDateFilter = (date: BaremetricsCalendar): boolean => {
    let newStartDate = this.validateDate(date);
    //Only let dates that are the beginning of the Week show up
    if (this.schedule.IntervalType == this.IntervalTypes.Week && newStartDate.getDate() !== this.cvConfigurationService.weekStart) {
      return false;
    }

    //Only let dates that are the beginning of the month show up
    if (this.schedule.IntervalType == this.IntervalTypes.Month && newStartDate.getDate() !== 1) {
      return false;
    }

    return true;
  }

  _endDateFilter = (date: BaremetricsCalendar): boolean => {
    let newEndDate = this.validateDate(date);
    //Only let dates that are the end of the week show up
    if (this.schedule.IntervalType == this.IntervalTypes.Week && (newEndDate.getDate() + 1) % 7 !== this.cvConfigurationService.weekStart) {
      return false;
    }

    //Only let dates that are the end of the month show up
    if (this.schedule.IntervalType == this.IntervalTypes.Month) {
      let dateCopy = moment(newEndDate); //Make a copy of the date as endOf (next line) modifies the date
      let currentEndOfMonthDate = dateCopy.endOf('month').date();
      if (newEndDate.getDate() !== currentEndOfMonthDate)
        return false;
    }

    return true;
  }

  validateDate(date: BaremetricsCalendar) {
    return date.current_date ? date.current_date.toDate() : null;
  }

  _nearestWeekDate(x: number) {
    return 7 * Math.round(x / 7)
  }

  _nearestMonthDate(x: number) {
    var today = new Date();
    var daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return daysInMonth * Math.round(x / daysInMonth);
  }

  setDefaultStartDate() {
    if (this.schedule.StartDate == null || this.schedule.StartDate == "") {
      if (this.schedule.IntervalType == this.IntervalTypes.Week) {
        var todayDay = new Date().getDay(); //Get today's day of week
        var nearestDay = this._nearestWeekDate(new Date().getDay()); //Get the nearest day of week (will either be a 0 or a 7)
        this.schedule.StartDate = new Date().addDays(nearestDay - todayDay + this.cvConfigurationService.weekStart);
      }
      if (this.schedule.IntervalType == this.IntervalTypes.Month) {
        var todayDay = new Date().getDate();
        var nearestDay = this._nearestMonthDate(todayDay);
        this.schedule.StartDate = new Date().addDays(nearestDay - todayDay + 1);
      }
    }
  };

  setDefaultEndDate() {
    if (this.schedule.EndDate == null || this.schedule.EndDate == "") {
      if (this.schedule.IntervalType == this.IntervalTypes.Week) {
        if (this.schedule.StartDate != null && this.schedule.StartDate != "") {
          this.schedule.EndDate = new Date(this.schedule.StartDate).addDays(6);
        }
        else {
          var todayDay = new Date().getDay(); //Get today's day of week
          var nearestDay = this._nearestWeekDate(new Date().getDay());
          this.schedule.EndDate = new Date().addDays(nearestDay - todayDay);
        }
      }
      if (this.schedule.IntervalType == this.IntervalTypes.Month) {
        if (this.schedule.StartDate != null && this.schedule.StartDate != "") {
          this.schedule.EndDate = new Date(this.schedule.StartDate).addMonths(1).addDays(-1);
        }
        else {
          var todayDay = new Date().getDate();
          var nearestDay = this._nearestMonthDate(todayDay);
          this.schedule.EndDate = new Date().addDays(nearestDay - todayDay);
          if (this.schedule.EndDate < new Date()) {
            //Quirk:
            /* Adding a month seems to either match the current day or only add 30 days to the current date.
             * When I was testing this on July 15th, this defaulted to June 30. Simply using <date>.addMonths(1) set the date
             * to be July 30th instead of the 31st. By adding a day first, thus putting the current date to July 1st, adding a month to get to August 1st
             * then subtracting a day, I was able to get to the correct day of July 31st.
             */
            this.schedule.EndDate = new Date(this.schedule.EndDate).addDays(1).addMonths(1).addDays(-1);
          }
        }
      }
    }
  };

  ongoingChanged(): void {
    if (this.schedule.Ongoing)
      this.schedule.EndDate = null;
  }

  getButtonClass(value: string): string {
    return (this.schedule.IntervalType == value) ? 'active' : '';
  }

  setScheduleInterval(value: string): void {
    if (this.schedule.IntervalType == value) {
      return;
    } else {
      this.schedule.IntervalType = value;
    }
  }

  getFormattedDate(value) {
    return this.datePipe.transform(value, CVAngularDateFormat);
  }

  datePlaceHolder(): string{
    return this.gamesService.getLocalizedMessage('Select_Date', 'Select Date');
  }

  removeContentEditable(event) {
    const elementWithContentEditable = event.target.firstChild?.firstChild?.firstChild;
    if (elementWithContentEditable && elementWithContentEditable.setAttribute) {
        elementWithContentEditable.setAttribute('contenteditable', false);
    }
  }

  handleStartDateChanged(startDate: BaremetricsCalendar) {
    if (this.schedule.EndDate < startDate.current_date) {
      this.schedule.EndDate = null;
      this.earliestDate = startDate.current_date ? startDate.current_date.toDate() : null;
    }
  };

  adjustStartDate() {
      let startDate = new Date(this.schedule.StartDate);
      if (startDate.getHours() > 0) {
          startDate = new Date(startDate.setMinutes(startDate.getTimezoneOffset()))
      }
      this.schedule.StartDate = startDate;
  }
}
